import { Component, OnInit, Optional } from '@angular/core';
import { SummarizeOrderJeevesComponent, JeevesDeliveryAddressesService } from 'gung-standard-jeeves';
import {
  CustomerService,
  ProductService,
  AuthService,
  PriceService,
  CartRow,
  CheckoutRow,
  SelectedCustomerService,
  CustomerProductPrice,
  Product,
  PriceConfigService,
  CheckoutObject,
  GungGoogleTagManagerService
} from 'gung-standard';
import { DateUtilService } from 'gung-common';
import { mergeMap, map, switchMap, first } from 'rxjs';
import { forkJoin, of, from } from 'rxjs';
import { ColmecAddonsCartService } from '../../../../services/colmec-addons-cart.service';
import { flattenArray } from '../../../../utilities/cart-rows-extra-configure-helpers';

@Component({
    selector: 'colmec-summarize-order-jeeves',
    templateUrl: './colmec-summarize-order-jeeves.component.html',
    styleUrls: ['./colmec-summarize-order-jeeves.component.scss'],
    standalone: false
})
export class ColmecSummarizeOrderJeevesComponent extends SummarizeOrderJeevesComponent implements OnInit {
  // private expressFreightProductId = '26575';
  public hidePrice = false;

  extras: {
    hasAdditional: boolean;
    totalQty: number;
    spikes: { totalValue: number; totalQty: number; currencyCode: string };
    recycling: { totalValue: number; totalQty: number; currencyCode: string };
    siping: { totalValue: number; totalQty: number; currencyCode: string };
    // expressFreight: { totalValue: number; totalQty: number; currencyCode: string };
  };
  allRows: CheckoutRow[];
  autoAdded: any;
  additionalProductsMap: any;
  additionalProductsPricesMap: any;
  totalValue: number;

  mappedProductsAddons: Map<string, { addon: Product; price: CustomerProductPrice }[]>;

  constructor(
    protected customerService: CustomerService,
    protected productService: ProductService,
    protected authService: AuthService,
    protected priceService: PriceService,
    private colmecAddonsCartService: ColmecAddonsCartService,
    private selectedCustomerService: SelectedCustomerService,
    public dateUtilService: DateUtilService,
    private priceConfigService: PriceConfigService,
    protected jeevesDeliveryAddressesService: JeevesDeliveryAddressesService,
    @Optional()
    protected gungGoogleTagManagerService: GungGoogleTagManagerService
  ) {
    super(customerService, productService, authService, priceService, dateUtilService, jeevesDeliveryAddressesService, gungGoogleTagManagerService);
  }

  ngOnInit() {
    this.priceConfigService.hidePrice.subscribe(hidePrice => (this.hidePrice = hidePrice));
    super.ngOnInit();
    this.extras = {
      hasAdditional: false,
      totalQty: 0,
      siping: { totalValue: 0, totalQty: 0, currencyCode: 'SEK' },
      spikes: { totalValue: 0, totalQty: 0, currencyCode: 'SEK' },
      recycling: { totalValue: 0, totalQty: 0, currencyCode: 'SEK' }
      // expressFreight: { totalValue: 0, totalQty: 0, currencyCode: 'SEK' }
    };

    const source = from([this.checkout.rows]);
    this.totalValue = 0;
    source
      .pipe(
        mergeMap(rows =>
          forkJoin({
            rows: of(rows),
            products: this.productService.getFullProductsByIds(rows.map(row => row.id)).pipe(first())
          })
        ),
        mergeMap(({ rows, products }) => {
          const extraProductIds = [];
          products.forEach(product => {
            if (product.extra.pim) {
              if (product.extra.pim._colmecSpikesProduct) {
                extraProductIds.push(product.extra.pim._colmecSpikesProduct);
              }
              if (product.extra.pim._colmecSipingProduct) {
                extraProductIds.push(product.extra.pim._colmecSipingProduct);
              }
            }

            if (product.extra.ar.artnraddart) {
              extraProductIds.push(product.extra.ar.artnraddart);
            }
          });

          return forkJoin({
            rows: of(rows),
            products: of(products),
            extraProductIds: of(extraProductIds),
            autoAddedElements: of([])
          });
        }),
        mergeMap(({ rows, products, extraProductIds, autoAddedElements }) => {
          const autoProductIds = autoAddedElements.map(e => e.productId);
          const allIds = [...autoProductIds, ...extraProductIds];

          const mappedCartRows = rows.map(checkoutRow => {
            const rowMap: CartRow = {
              extra: checkoutRow.extra,
              qty: checkoutRow.quantity,
              productId: checkoutRow.id,
              timeUpdated: new Date(),
              timeAdded: new Date()
            };
            return rowMap;
          });
          return forkJoin({
            rows: of(rows),
            products: of(products),
            additionalProducts: this.productService.getFullProductsByIds(allIds).pipe(first()),
            autoAddedElements: of(autoAddedElements),
            prices:
              allIds.length === 0
                ? of([])
                : forkJoin(
                    allIds.map(id => {
                      return this.priceService.getCustomerPrice(id, this.checkout.billingCustomerId).pipe(first());
                    })
                  ),
            cartPrices: this.priceService.getCartRowPricesObservable(mappedCartRows).pipe(first())
            // expressFreightCustomerProductPrice: !!this.checkout.extra._colmecExpressDelivery
            //   ? this.priceService
            //       .getCustomerPrice(this.expressFreightProductId, this.checkout.billingCustomerId)
            //       .pipe(first())
            //   : of(null)
          });
        }),

        map(
          ({
            rows,
            products,
            additionalProducts,
            autoAddedElements,
            prices,
            cartPrices
            // expressFreightCustomerProductPrice
          }) => {
            this.extras.hasAdditional = autoAddedElements.length > 0;
            this.autoAdded = autoAddedElements;
            this.additionalProductsMap = this.additionalProductsMap || {};
            this.additionalProductsPricesMap = this.additionalProductsPricesMap || {};
            products.forEach(p => {
              this.additionalProductsMap[p.id] = p;
            });
            additionalProducts.forEach(p => {
              this.additionalProductsMap[p.id] = p;
            });
            prices.forEach(p => {
              this.additionalProductsPricesMap[p.productId] = p;
            });

            this.allRows = [];

            rows.forEach(checkoutRow => {
              this.allRows.push(checkoutRow);

              if (checkoutRow.extra._colmecDubbingSelected) {
                const product = this.additionalProductsMap[checkoutRow.id];
                const price = this.additionalProductsPricesMap[product.extra.pim._colmecSpikesProduct];
                this.extras.spikes.totalValue += price.customerNetPrice.value * checkoutRow.quantity;
                this.extras.spikes.totalQty += 1 * checkoutRow.quantity;
                this.extras.spikes.currencyCode = price.customerNetPrice.currencyCode;
                this.extras.totalQty += 1;
              }

              if (checkoutRow.extra._colmecSipingSelected) {
                const product = this.additionalProductsMap[checkoutRow.id];
                const price = this.additionalProductsPricesMap[product.extra.pim._colmecSipingProduct];
                this.extras.siping.totalValue += price.customerNetPrice.value * checkoutRow.quantity;
                this.extras.siping.totalQty += 1 * checkoutRow.quantity;
                this.extras.siping.currencyCode = price.customerNetPrice.currencyCode;
                this.extras.totalQty += 1;
              }

              // if (this.checkout.extra._colmecExpressDelivery) {
              //   const product = this.additionalProductsMap[checkoutRow.id];
              //   // Expressfrakt pris ska ej räknas för varugruppkod = 191
              //   if (product.extra.ar.varugruppkod !== '191') {
              //     this.extras.expressFreight.totalValue +=
              //       checkoutRow.quantity * expressFreightCustomerProductPrice.customerNetPrice.value;
              //     this.extras.expressFreight.totalQty += 1 * checkoutRow.quantity;
              //     this.extras.expressFreight.currencyCode =
              //       expressFreightCustomerProductPrice.customerGrossPrice.currencyCode;
              //     this.extras.totalQty += 1;
              //   }
              // }

              if (this.productsMap[checkoutRow.id].extra.ar.artnraddart) {
                this.extras.recycling.totalValue = 0;
                this.extras.recycling.totalQty += 1;
                this.extras.totalQty += 1;
              }
            });

            this.totalValue +=
              this.extras.siping.totalValue + this.extras.spikes.totalValue; //+ this.extras.expressFreight.totalValue;
            this.autoAdded.forEach(e => {
              this.totalValue += this.additionalProductsPricesMap[e.productId].customerNetPrice.value * e.qty;
            });

            cartPrices.forEach(p => {
              this.totalValue += p.cartRowTotalPriceInclRowDiscount.value;
            });
          }
        )
      )
      .subscribe();

    forkJoin({
      selectedCustomer: this.selectedCustomerService.getSelectedCustomer().pipe(first()),
      addOnsByProducts: this.colmecAddonsCartService.getAddOnProductsByParentProduct().pipe(first())
    })
      .pipe(
        first(),
        switchMap(result => {
          // flatten array of all addonids
          const allAddonIds: string[] = flattenArray(result.addOnsByProducts.map(x => x.addons.map(y => y.product.id)));

          return forkJoin([
            of(result.addOnsByProducts),
            this.priceService.getCustomerPrices(allAddonIds, result.selectedCustomer.id).pipe(first())
          ]);
        })
      )
      .pipe(first())
      .subscribe(([addOnsByProducts, prices]) => {
        this.mappedProductsAddons = new Map<string, { addon: Product; price: CustomerProductPrice }[]>();

        addOnsByProducts.forEach(item => {
          const addonPriceByParentProduct: { addon: Product; price: CustomerProductPrice }[] = [];
          item.addons.forEach(addonProduct => {
            addonPriceByParentProduct.push({
              addon: addonProduct.product,
              price: prices.find(p => p.productId === addonProduct.product.id)
            });
          });
          this.mappedProductsAddons.set(item.productId, addonPriceByParentProduct);
        });
      });
  }

  public getBtnTexts(checkout: CheckoutObject): { next: string | void; prev: string | void } | void {
    return {
      next: 'SUBMIT_ORDER',
      prev: null
    };
  }
}
